// ApplicantStep.js — Mortgage Matrix Broker CRM
// v9.5.0 — sharper property-style applicant UI
// -----------------------------------------------------------------------------------
// ✅ Keeps v9.1 “no-shake” touched + merge hydration logic
// ✅ Credit Guide panel AFTER Email + full-width
// ✅ crmRef/opportunityId from props
// ✅ Localhost-friendly CG API base
// ✅ DOB normalisation preserved
// ✅ Dependants COUNT still supported (Applicant 1)
// ✅ Dependants Ages array capture (age-only; no name/DOB required)
// ✅ Sync applicant1/applicant2 objects to parent (covers submit payload builders)
// ✅ FIX: Dependants "5+" now works (treated as 5 for age-capture sizing)
// ✅ FIX: Also writes Mercury-compatible field numberOfDependents (and numberOfDependants fallback) so dependants show in Mercury
// ✅ NEW: hydrates dependant ages from lookup/manual prefill
// ✅ NEW: supports dependantAges / dependentsAges / dependantsAges aliases
// ✅ NEW: local Back / Next buttons removed so shared nav from BrokerCRM is the only nav
// ✅ UPDATED: Applicant selector + form fields now match sharper Property screen styling

import React, { useState, useEffect, useRef } from "react"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

const LOOKUP_URL =
  "https://7r69rk0g8a.execute-api.ap-southeast-2.amazonaws.com/default/MortgageMatrixGetLeadByMobile"

// ------------------------------
// Credit Guide Consent API base (LOCALHOST FRIENDLY)
// ------------------------------
const CG_API_BASE = String(process.env.REACT_APP_CG_API_BASE || "").replace(/\/$/, "")
const cgUrl = (path) => `${CG_API_BASE}${path}`

// ------------------------------
// Helpers
// ------------------------------
const normaliseAU = (n = "") => {
  let x = String(n || "").replace(/\D/g, "")
  if (x.startsWith("61")) x = "0" + x.slice(2)
  if (!x.startsWith("0")) x = "0" + x
  return x.slice(0, 10)
}

// ✅ Robust: always returns yyyy-mm-dd for <input type="date">
const normaliseDobValue = (raw) => {
  if (raw === null || raw === undefined || raw === "") return ""

  if (typeof raw === "object") {
    const inner = raw?.value ?? raw?.dob ?? raw?.dateOfBirth ?? raw?.birthDate
    return normaliseDobValue(inner)
  }

  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) return raw.trim()

  if (typeof raw === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw.trim())) {
    const [d, m, y] = raw.trim().split("/")
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  }

  if (typeof raw === "string" && /^\d{1,2}-\d{1,2}-\d{4}$/.test(raw.trim())) {
    const [d, m, y] = raw.trim().split("-")
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  }

  if (typeof raw === "string" && /^\d{4}\/\d{1,2}\/\d{1,2}$/.test(raw.trim())) {
    const [y, m, d] = raw.trim().split("/")
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  }

  if (typeof raw === "string") {
    const s = raw.trim()

    const mj = s.match(/\/Date\((\d+)\)\//i)
    if (mj?.[1]) {
      const ms = Number(mj[1])
      if (Number.isFinite(ms)) {
        const d = new Date(ms)
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10)
      }
    }

    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.slice(0, 10)

    const dt = new Date(s)
    if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10)
  }

  const n = Number(raw)
  if (!Number.isFinite(n)) return ""

  const ms = n < 1e12 ? n * 1000 : n
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ""
  return d.toISOString().slice(0, 10)
}

const parseDobFromMercury = (raw) => {
  const dob = normaliseDobValue(raw)
  if (!dob) return { dobDay: "", dobMonth: "", dobYear: "", dob: "" }
  const [y, m, d] = dob.split("-")
  return { dobDay: d, dobMonth: m, dobYear: y, dob }
}

const clampInt = (v, min, max) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return ""
  return String(Math.max(min, Math.min(max, Math.round(n))))
}

const parseDepCount = (v) => {
  const s = String(v ?? "").trim()
  if (!s) return 0
  if (s.includes("+")) {
    const n = Number(s.replace(/[^\d]/g, ""))
    return Number.isFinite(n) ? n : 0
  }
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}

const normaliseDependantAges = (raw) => {
  const arr = Array.isArray(raw) ? raw : []
  return arr.map((x) => {
    if (x === "" || x === null || x === undefined) return ""
    const n = Number(x)
    if (!Number.isFinite(n)) return ""
    return clampInt(n, 0, 30)
  })
}

const pickDependantAges = (obj = {}) => {
  return normaliseDependantAges(
    obj?.dependantAges ??
      obj?.dependentsAges ??
      obj?.dependantsAges ??
      []
  )
}

const createApplicantTemplate = (id) => ({
  id,
  contactId: "",
  title: "",
  firstName: "",
  middleName: "",
  lastName: "",
  dob: "",
  gender: "",
  mobile: "",
  email: "",
  relationship: "",
  residency: "",
  creditHistory: "",
  dependants: "",
  dependantAges: [],
  numberOfDependents: "",
  numberOfDependants: "",
})

const normaliseApplicants = (arr = []) =>
  (arr || []).map((a, idx) => {
    const merged = { ...createApplicantTemplate(a?.id ?? idx + 1), ...a }

    merged.dob = normaliseDobValue(merged.dob ?? merged.dateOfBirth ?? merged.birthDate)

    if (merged.dependants !== null && merged.dependants !== undefined && merged.dependants !== "") {
      merged.dependants = String(merged.dependants)
    } else {
      merged.dependants = merged.dependants ?? ""
    }

    merged.dependantAges = pickDependantAges(merged)

    const depCount = parseDepCount(
      merged.dependants ?? merged.numberOfDependents ?? merged.numberOfDependants
    )

    if (Number.isFinite(depCount) && depCount >= 0) {
      const next = [...merged.dependantAges]
      while (next.length < depCount) next.push("")
      if (next.length > depCount) next.length = depCount
      merged.dependantAges = next
    }

    const depCountStr = depCount === 0 && merged.dependants === "" ? "" : String(depCount)
    merged.numberOfDependents =
      merged.numberOfDependents !== "" ? String(merged.numberOfDependents) : depCountStr
    merged.numberOfDependants =
      merged.numberOfDependants !== "" ? String(merged.numberOfDependants) : depCountStr

    return merged
  })

const applicantsSig = (arr = []) =>
  JSON.stringify(
    (arr || []).map((a) => ({
      id: a?.id ?? "",
      contactId: a?.contactId ?? "",
      title: a?.title ?? "",
      firstName: a?.firstName ?? "",
      middleName: a?.middleName ?? "",
      lastName: a?.lastName ?? "",
      dob: normaliseDobValue(a?.dob ?? a?.dateOfBirth ?? a?.birthDate),
      gender: a?.gender ?? "",
      mobile: a?.mobile ?? "",
      email: a?.email ?? "",
      relationship: a?.relationship ?? "",
      residency: a?.residency ?? "",
      creditHistory: a?.creditHistory ?? "",
      dependants: a?.dependants ?? "",
      dependantAges: Array.isArray(a?.dependantAges) ? a.dependantAges : [],
      numberOfDependents: a?.numberOfDependents ?? "",
      numberOfDependants: a?.numberOfDependants ?? "",
    }))
  )

function ApplicantStep({
  data = [],
  updateCrmData,
  onNext,
  onBack,
  crmRef: crmRefProp = "",
  opportunityId: opportunityIdProp = "",
}) {
  const lookupLock = useRef(false)

  const touchedRef = useRef({})
  const markTouched = (idx, field) => {
    touchedRef.current[`${idx}.${field}`] = true
  }
  const isTouched = (idx, field) => !!touchedRef.current[`${idx}.${signatureFieldKey(field)}`]

  function signatureFieldKey(field) {
    if (field === "dateOfBirth" || field === "birthDate") return "dob"
    return field
  }

  const [applicants, setApplicants] = useState(
    Array.isArray(data) && data.length > 0 ? normaliseApplicants(data) : [createApplicantTemplate(1)]
  )

  const [activeApplicant, setActiveApplicant] = useState(0)
  const step1MobileLoaded = !!data?.[0]?.mobile
  const [dobError, setDobError] = useState("")

  const UI = {
    ACCENT: "#4EDCF6",
    ACCENT_SOFT: "rgba(78,220,246,0.12)",
    DARK: "#141414",
    SOFT_DARK: "rgba(17,24,39,0.72)",
    MUTED: "#6B7280",
    PAGE: "#F5F7FA",
    WHITE: "#FFFFFF",
    PANEL: "rgba(255,255,255,0.94)",
    BORDER: "rgba(17,24,39,0.10)",
    BORDER_STRONG: "rgba(17,24,39,0.18)",
    INPUT_BORDER: "rgba(17,24,39,0.22)",
    INPUT_BORDER_FOCUS: "rgba(17,24,39,0.55)",
    SHADOW_SOFT: "0 8px 24px rgba(15,23,42,0.05)",
    SHADOW_CARD: "0 10px 24px rgba(20,20,20,0.05)",
    SHADOW_ACTIVE: "0 10px 24px rgba(20,20,20,0.08)",
    SHADOW_CTA: "0 10px 24px rgba(78, 220, 246, 0.22)",
  }

  const [cgStatus, setCgStatus] = useState(null)
  const [cgBusy, setCgBusy] = useState({ a1: false, a2: false, both: false })

  const crmRef = String(crmRefProp || "").trim()
  const opportunityId = String(opportunityIdProp || "").trim()

  const hasApplicant2 =
    applicants.length >= 2 &&
    !!(applicants?.[1]?.firstName || applicants?.[1]?.lastName || applicants?.[1]?.mobile)

  const safeLookupKey = crmRef || normaliseAU(applicants?.[0]?.mobile || "")

  const refreshCgStatus = async () => {
    if (!safeLookupKey) return
    try {
      const res = await fetch(cgUrl(`/api/consent/credit-guide/status?crmRef=${encodeURIComponent(safeLookupKey)}`))
      if (!res.ok) return
      const json = await res.json()
      setCgStatus(json)
    } catch {}
  }

  useEffect(() => {
    refreshCgStatus()
    if (!safeLookupKey) return
    const t = setInterval(refreshCgStatus, 12000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeLookupKey])

  const getCgUi = (s) => {
    const accepted = Boolean(s?.complete || s?.consentAt)
    const sent = Boolean(s?.sentAt) && !accepted

    if (accepted) {
      return {
        label: "Credit Guide Accepted ✅",
        disabled: true,
        variant: "disabled",
        subText: s?.consentAt ? `Accepted: ${new Date(s.consentAt).toLocaleString()}` : "Accepted",
      }
    }

    if (sent) {
      return {
        label: "Resend Credit Guide",
        disabled: false,
        variant: "outline",
        subText: `Sent: ${new Date(s.sentAt).toLocaleString()} — waiting on client`,
      }
    }

    return {
      label: "Send Credit Guide (SMS)",
      disabled: false,
      variant: "primary",
      subText: "Send the Credit Guide link for eConsent",
    }
  }

  const sendCreditGuide = async (applicantIndex) => {
    const idx = applicantIndex - 1
    const a = applicants?.[idx]
    if (!a) return toast.error(`Applicant ${applicantIndex} not available`)

    const mobileRaw = a?.mobile || ""
    const mobile = normaliseAU(mobileRaw)

    if (!mobile || mobile.length < 8) {
      return toast.error(`Enter a valid mobile for Applicant ${applicantIndex}`)
    }

    const busyKey = applicantIndex === 1 ? "a1" : "a2"
    setCgBusy((p) => ({ ...p, [busyKey]: true }))

    try {
      const res = await fetch(cgUrl("/api/consent/credit-guide/send-link"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          crmRef: safeLookupKey,
          opportunityId,
          applicantIndex,
          applicantName: `${a?.firstName || ""} ${a?.lastName || ""}`.trim(),
          contactId: a?.contactId || "",
          mobile,
          email: a?.email || "",
        }),
      })

      if (!res.ok) throw new Error(await res.text())

      toast.success(`Credit Guide sent to Applicant ${applicantIndex}`)
      await refreshCgStatus()
    } catch (e) {
      toast.error(e?.message || "Failed to send Credit Guide")
    } finally {
      setCgBusy((p) => ({ ...p, [busyKey]: false }))
    }
  }

  const sendToBoth = async () => {
    setCgBusy((p) => ({ ...p, both: true }))
    try {
      await sendCreditGuide(1)
      if (hasApplicant2) {
        const a2Mobile = normaliseAU(applicants?.[1]?.mobile || "")
        if (a2Mobile) await sendCreditGuide(2)
        else toast.warn("Applicant 2 has no mobile number")
      }
    } finally {
      setCgBusy((p) => ({ ...p, both: false }))
    }
  }

  const primaryBtn = {
    minHeight: 48,
    padding: "0 18px",
    background: UI.ACCENT,
    border: "none",
    borderRadius: 12,
    cursor: "pointer",
    fontWeight: 700,
    color: UI.DARK,
    boxShadow: UI.SHADOW_CTA,
  }

  const ghostBtn = {
    minHeight: 48,
    padding: "0 18px",
    borderRadius: 12,
    border: `1.5px solid ${UI.BORDER}`,
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700,
    color: UI.DARK,
  }

  const CreditGuidePanel = ({ applicantIndex }) => {
    const k = applicantIndex === 1 ? "a1" : "a2"
    const s = cgStatus?.[k]
    const ui = getCgUi(s)
    const busyKey = applicantIndex === 1 ? "a1" : "a2"
    const isBusy = cgBusy[busyKey]

    const btnStyle =
      ui.variant === "primary"
        ? { ...primaryBtn, width: "100%" }
        : ui.variant === "outline"
          ? { ...ghostBtn, width: "100%" }
          : {
              ...ghostBtn,
              width: "100%",
              cursor: "not-allowed",
              opacity: 0.75,
              background: "#F3F4F6",
            }

    return (
      <div
        style={{
          width: "100%",
          padding: 18,
          borderRadius: 14,
          border: `1px solid ${UI.BORDER}`,
          background: "#fff",
          boxShadow: UI.SHADOW_CARD,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontWeight: 800, color: UI.DARK }}>Credit Guide eConsent</div>

          <div
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 800,
              background: ui.variant === "disabled" ? UI.ACCENT_SOFT : "#F7F8F8",
              border: ui.variant === "disabled" ? `1.5px solid ${UI.ACCENT}` : `1px solid ${UI.BORDER}`,
              color: UI.DARK,
              whiteSpace: "nowrap",
            }}
          >
            {ui.variant === "disabled" ? "Accepted" : s?.sentAt ? "Sent" : "Not sent"}
          </div>
        </div>

        <button
          type="button"
          onClick={() => sendCreditGuide(applicantIndex)}
          disabled={ui.disabled || isBusy}
          style={btnStyle}
        >
          {isBusy ? "Sending..." : ui.label}
        </button>

        <div style={{ marginTop: 10, fontSize: 12, color: UI.MUTED, fontWeight: 600 }}>{ui.subText}</div>

        {s?.referenceId && (
          <div style={{ marginTop: 8, fontSize: 12, color: UI.MUTED, fontWeight: 600 }}>
            Ref: <span style={{ fontWeight: 900, color: UI.DARK }}>{s.referenceId}</span>
          </div>
        )}

        {ui.variant === "disabled" && (
          <button
            type="button"
            onClick={() => sendCreditGuide(applicantIndex)}
            style={{
              marginTop: 10,
              background: "transparent",
              border: "none",
              padding: 0,
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 800,
              color: "rgba(17,24,39,0.75)",
            }}
          >
            Resend anyway
          </button>
        )}
      </div>
    )
  }

  const lastHydrateSigRef = useRef("")
  useEffect(() => {
    if (!Array.isArray(data)) return

    const sig = applicantsSig(data)
    if (sig === lastHydrateSigRef.current) return
    lastHydrateSigRef.current = sig

    setApplicants((prev) => {
      const incoming = normaliseApplicants(data)
      const next = [...prev]

      for (let i = 0; i < incoming.length; i++) {
        if (!next[i]) next[i] = createApplicantTemplate(i + 1)

        const merged = { ...next[i] }
        Object.keys(incoming[i] || {}).forEach((key) => {
          const protectedKey = signatureFieldKey(key)
          if (isTouched(i, protectedKey)) return

          let val = incoming[i][key]
          if (val === undefined) return

          if (protectedKey === "dob") val = normaliseDobValue(val)
          if (protectedKey === "dependants" && val !== "" && val !== null && val !== undefined) {
            val = String(val)
          }

          if (protectedKey === "dependantAges") {
            if (isTouched(i, "dependantAges")) return
            val = pickDependantAges(incoming[i])
          }

          merged[protectedKey] = val
        })

        merged.dob = normaliseDobValue(merged.dob ?? incoming[i].dateOfBirth ?? incoming[i].birthDate)

        if (!isTouched(i, "dependantAges")) {
          merged.dependantAges = pickDependantAges(merged)
        }

        const depCount = parseDepCount(
          merged.dependants ?? merged.numberOfDependents ?? merged.numberOfDependants
        )
        const depCountStr = depCount === 0 && (merged.dependants ?? "") === "" ? "" : String(depCount)

        if (!isTouched(i, "numberOfDependents")) merged.numberOfDependents = depCountStr
        if (!isTouched(i, "numberOfDependants")) merged.numberOfDependants = depCountStr

        if (!isTouched(i, "dependantAges")) {
          const ages = Array.isArray(merged.dependantAges) ? [...merged.dependantAges] : []
          while (ages.length < depCount) ages.push("")
          if (ages.length > depCount) ages.length = depCount
          merged.dependantAges = ages
        }

        next[i] = merged
      }

      if (!next[0]) next[0] = createApplicantTemplate(1)

      return normaliseApplicants(next)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const lastPushedSigRef = useRef("")
  useEffect(() => {
    if (typeof updateCrmData !== "function") return

    const sig = applicantsSig(applicants)
    if (sig === lastPushedSigRef.current) return
    lastPushedSigRef.current = sig

    const normalised = normaliseApplicants(applicants)

    updateCrmData("applicants", normalised)
    updateCrmData("applicant1", normalised?.[0] || createApplicantTemplate(1))
    updateCrmData("applicant2", normalised?.[1] || createApplicantTemplate(2))
  }, [applicants, updateCrmData])

  const applyDobValidation = (_index, dobStr) => {
    if (!dobStr) {
      setDobError("Date of birth required")
      return
    }

    const dob = new Date(dobStr)
    if (Number.isNaN(dob.getTime())) {
      setDobError("Invalid date of birth")
      return
    }

    const today = new Date()
    const age = today.getFullYear() - dob.getFullYear()
    const monthDiff = today.getMonth() - dob.getMonth()
    const dayDiff = today.getDate() - dob.getDate()

    const isUnder18 =
      age < 18 || (age === 18 && monthDiff < 0) || (age === 18 && monthDiff === 0 && dayDiff < 0)

    if (isUnder18) setDobError("Applicant must be at least 18 years old.")
    else setDobError("")
  }

  const handleChange = (idx, field, value) => {
    markTouched(idx, signatureFieldKey(field))
    const next = [...applicants]

    if (idx === 0 && field === "mobile") return

    if (field === "dob") {
      next[idx] = { ...next[idx], dob: normaliseDobValue(value) }
      setApplicants(next)
      return
    }

    if (field === "dependants") {
      const safeCount = parseDepCount(value)

      const currentAges = Array.isArray(next[idx]?.dependantAges) ? [...next[idx].dependantAges] : []
      while (currentAges.length < safeCount) currentAges.push("")
      if (currentAges.length > safeCount) currentAges.length = safeCount

      const depCountStr = value === "" ? "" : String(safeCount)

      next[idx] = {
        ...next[idx],
        dependants: value === "" ? "" : String(value),
        dependantAges: currentAges,
        numberOfDependents: depCountStr,
        numberOfDependants: depCountStr,
      }
      setApplicants(next)
      return
    }

    if (field === "dependantAges") {
      next[idx] = { ...next[idx], dependantAges: Array.isArray(value) ? value : [] }
      setApplicants(next)
      return
    }

    next[idx] = { ...next[idx], [field]: value }
    setApplicants(next)
  }

  const addApplicant = () => {
    if (applicants.length >= 2) return
    setApplicants((prev) => [...prev, createApplicantTemplate(2)])
    setActiveApplicant(1)
  }

  const calculateProgress = (a, index) => {
    const base = ["title", "firstName", "lastName", "dob", "mobile", "email", "relationship", "residency", "creditHistory"]
    const required = index === 0 ? [...base, "dependants"] : base
    const filled = required.filter((f) => a?.[f] && a?.[f] !== "").length
    return Math.round((filled / required.length) * 100)
  }

  const fetchExisting = async () => {
    if (step1MobileLoaded || lookupLock.current) return

    const mobileInput = applicants[0]?.mobile || ""
    if (!mobileInput || mobileInput.length < 8) return toast.error("Enter mobile before prefilling")

    lookupLock.current = true
    const mobile = normaliseAU(mobileInput)

    try {
      const resp = await fetch(LOOKUP_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile }),
      })

      const txt = await resp.text()
      let respData = {}

      try {
        respData = JSON.parse(txt)
      } catch {
        toast.error("Invalid Mercury response")
        return
      }

      if (respData?.body && typeof respData.body === "string") {
        try {
          respData = JSON.parse(respData.body)
        } catch {}
      }

      if (!respData.success) return toast.error("Lookup failed")
      if (!respData.found) return toast.warn("No match found")

      toast.success("Prefilled from Mercury")

      const next = [...applicants]
      const dob1 = parseDobFromMercury(respData.dateOfBirth ?? respData.dob ?? respData.birthDate)

      const depRaw =
        respData.dependants ??
        respData.numberOfDependents ??
        respData.numberOfDependants ??
        next[0].dependants

      const depCount = parseDepCount(depRaw)
      const depCountStr = depRaw === "" || depRaw === null || depRaw === undefined ? "" : String(depCount)
      const depAges = pickDependantAges(respData)

      next[0] = {
        ...next[0],
        contactId: respData.contactId || next[0].contactId,
        title: respData.title || next[0].title,
        firstName: respData.firstName || next[0].firstName,
        lastName: respData.lastName || next[0].lastName,
        email: respData.email || next[0].email,
        mobile,
        relationship: respData.maritalStatus || next[0].relationship,
        gender: respData.gender || next[0].gender,
        dependants: depRaw ?? next[0].dependants,
        dependantAges: depAges,
        numberOfDependents: depCountStr,
        numberOfDependants: depCountStr,
        dob: dob1.dob,
      }

      if (respData.coApplicant) {
        const dob2 = parseDobFromMercury(
          respData.coApplicant.dateOfBirth ?? respData.coApplicant.dob ?? respData.coApplicant.birthDate
        )
        if (next.length < 2) next.push(createApplicantTemplate(2))

        const coDepRaw =
          respData.coApplicant.dependants ??
          respData.coApplicant.numberOfDependents ??
          respData.coApplicant.numberOfDependants ??
          next[1].dependants

        const coDepCount = parseDepCount(coDepRaw)
        const coDepCountStr =
          coDepRaw === "" || coDepRaw === null || coDepRaw === undefined ? "" : String(coDepCount)

        next[1] = {
          ...next[1],
          contactId: respData.coApplicant.contactId,
          title: respData.coApplicant.title,
          firstName: respData.coApplicant.firstName,
          lastName: respData.coApplicant.lastName,
          email: respData.coApplicant.email,
          mobile: respData.coApplicant.mobile,
          relationship: respData.coApplicant.maritalStatus,
          gender: respData.coApplicant.gender,
          dependants: coDepRaw ?? next[1].dependants,
          dependantAges: pickDependantAges(respData.coApplicant),
          numberOfDependents: coDepCountStr,
          numberOfDependants: coDepCountStr,
          dob: dob2.dob,
        }
      }

      setApplicants(normaliseApplicants(next))
    } catch {
      toast.error("Prefill error")
    } finally {
      lookupLock.current = false
    }
  }

  const pageStyle = {
    padding: 8,
    maxWidth: 980,
  }

  const h2Style = {
    margin: "0 0 14px 0",
    fontSize: 28,
    fontWeight: 750,
    color: UI.DARK,
    letterSpacing: "-0.02em",
  }

  const smallMuted = {
    margin: "6px 0 0 0",
    color: UI.MUTED,
    fontSize: 13,
    fontWeight: 500,
  }

  const addBtn = {
    minHeight: 92,
    padding: "0 18px",
    borderRadius: 12,
    border: `1.5px solid rgba(17,17,17,0.12)`,
    background: "#ffffff",
    cursor: "pointer",
    width: "100%",
    fontWeight: 700,
    fontSize: 15,
    color: UI.DARK,
    boxShadow: "none",
  }

  const tabWrap = {
    display: "grid",
    gridTemplateColumns: applicants.length < 2 ? "1fr 220px" : "1fr 1fr",
    gap: 14,
    marginBottom: 16,
    alignItems: "stretch",
  }

  const tabCard = (active) => ({
    cursor: "pointer",
    minHeight: 92,
    padding: 18,
    borderRadius: 12,
    background: active ? "rgba(17,17,17,0.035)" : "#ffffff",
    border: active ? "2px solid #141414" : `1.5px solid rgba(17,17,17,0.12)`,
    boxShadow: active ? UI.SHADOW_ACTIVE : "none",
    transition: "all 0.16s ease",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  })

  const progressTrack = {
    height: 8,
    background: "#E5E7EB",
    borderRadius: 999,
    overflow: "hidden",
  }

  const formCard = {
    background: "#ffffff",
    padding: 20,
    borderRadius: 16,
    border: `1px solid ${UI.BORDER}`,
    boxShadow: UI.SHADOW_CARD,
    marginTop: 6,
  }

  const sectionTitle = {
    margin: "0 0 14px 0",
    fontSize: 18,
    fontWeight: 750,
    color: UI.DARK,
    letterSpacing: "-0.015em",
  }

  const grid2 = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 22,
    alignItems: "start",
  }

  const fieldGrid = {
    display: "grid",
    gap: 14,
  }

  const labelStyle = {
    display: "block",
    marginBottom: 8,
    fontWeight: 700,
    color: UI.DARK,
    fontSize: 14,
    letterSpacing: "-0.01em",
  }

  const inputStyle = {
    width: "100%",
    height: 52,
    padding: "10px 14px",
    borderRadius: 12,
    border: `1.5px solid ${UI.INPUT_BORDER}`,
    fontSize: 15,
    fontWeight: 600,
    background: "#fff",
    outline: "none",
    boxShadow: "none",
    color: UI.DARK,
  }

  const selectStyle = {
    ...inputStyle,
    paddingRight: 12,
    appearance: "auto",
  }

  const helpTextError = {
    color: "#DC2626",
    marginTop: 6,
    fontWeight: 600,
    fontSize: 13,
  }

  const dobDisplayValue = normaliseDobValue(applicants?.[activeApplicant]?.dob) || ""
  const depCountA1 = parseDepCount(applicants?.[0]?.dependants)
  const depAgesA1 = Array.isArray(applicants?.[0]?.dependantAges) ? applicants[0].dependantAges : []

  return (
    <div style={pageStyle}>
      <h2 style={h2Style}>Loan Applicants</h2>

      {!step1MobileLoaded && (
        <button onClick={fetchExisting} style={{ ...primaryBtn, marginBottom: 16 }}>
          ↻ Prefill from Mercury
        </button>
      )}

      <div style={tabWrap}>
        {applicants.map((a, i) => {
          const progress = calculateProgress(a, i)
          const active = i === activeApplicant

          return (
            <div
              key={a.id}
              onClick={() => setActiveApplicant(i)}
              style={tabCard(active)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setActiveApplicant(i)
              }}
            >
              <div>
                <h4
                  style={{
                    margin: "0 0 6px 0",
                    fontSize: 18,
                    fontWeight: 800,
                    color: UI.DARK,
                    letterSpacing: "-0.015em",
                  }}
                >
                  Applicant {i + 1}
                </h4>

                <p
                  style={{
                    margin: 0,
                    fontWeight: 700,
                    color: UI.DARK,
                    fontSize: 15,
                  }}
                >
                  {(a.firstName || a.lastName) ? `${a.firstName || ""} ${a.lastName || ""}`.trim() : "No name added"}
                </p>

                <p style={{ margin: "6px 0 12px 0", color: UI.MUTED, fontWeight: 500, fontSize: 13 }}>
                  {a.email || "No email added"}
                </p>
              </div>

              <div>
                <div style={progressTrack}>
                  <div style={{ width: `${progress}%`, height: "100%", background: UI.ACCENT }} />
                </div>
                <small style={{ fontSize: 13, color: UI.MUTED, fontWeight: 700 }}>{progress}% Complete</small>
              </div>
            </div>
          )
        })}

        {applicants.length < 2 && (
          <button onClick={addApplicant} style={addBtn}>
            + Add Applicant
          </button>
        )}
      </div>

      {applicants[activeApplicant] && (
        <div style={formCard}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 8,
            }}
          >
            <h3
              style={{
                margin: "0 0 6px 0",
                fontSize: 22,
                fontWeight: 800,
                color: UI.DARK,
                letterSpacing: "-0.02em",
              }}
            >
              Applicant {activeApplicant + 1} Details
            </h3>

            {hasApplicant2 && (
              <button onClick={sendToBoth} style={{ ...ghostBtn }} disabled={cgBusy.both}>
                {cgBusy.both ? "Sending..." : "Send Credit Guide to Both"}
              </button>
            )}
          </div>

          <div style={grid2}>
            <div>
              <h4 style={sectionTitle}>Contact details</h4>

              <div style={fieldGrid}>
                <div>
                  <label style={labelStyle}>Title</label>
                  <select
                    value={applicants[activeApplicant].title}
                    onChange={(e) => handleChange(activeApplicant, "title", e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">Select</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Miss">Miss</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>First Name</label>
                  <input
                    value={applicants[activeApplicant].firstName}
                    onChange={(e) => handleChange(activeApplicant, "firstName", e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Middle Name</label>
                  <input
                    value={applicants[activeApplicant].middleName}
                    onChange={(e) => handleChange(activeApplicant, "middleName", e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Last Name</label>
                  <input
                    value={applicants[activeApplicant].lastName}
                    onChange={(e) => handleChange(activeApplicant, "lastName", e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Mobile</label>
                  <input
                    value={applicants[activeApplicant].mobile}
                    disabled={activeApplicant === 0}
                    onChange={(e) => activeApplicant !== 0 && handleChange(activeApplicant, "mobile", e.target.value)}
                    style={{
                      ...inputStyle,
                      background: activeApplicant === 0 ? "#F3F4F6" : "#ffffff",
                      cursor: activeApplicant === 0 ? "not-allowed" : "text",
                      opacity: activeApplicant === 0 ? 0.9 : 1,
                    }}
                  />
                  {activeApplicant === 0 && <p style={smallMuted}></p>}
                </div>

                <div>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={applicants[activeApplicant].email}
                    onChange={(e) => handleChange(activeApplicant, "email", e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            <div>
              <h4 style={sectionTitle}>Personal details</h4>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>DOB</label>

                <div style={{ position: "relative", width: "100%" }}>
                  <input
                    type="date"
                    value={dobDisplayValue}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => {
                      const value = e.target.value
                      markTouched(activeApplicant, "dob")

                      if (!value) {
                        handleChange(activeApplicant, "dob", "")
                        setDobError("Date of birth required")
                        return
                      }

                      const dt = new Date(value)
                      if (Number.isNaN(dt.getTime())) {
                        handleChange(activeApplicant, "dob", "")
                        setDobError("Invalid date of birth")
                        return
                      }

                      handleChange(activeApplicant, "dob", value)
                      applyDobValidation(activeApplicant, value)
                    }}
                    style={{
                      ...inputStyle,
                      paddingRight: 44,
                      border: dobError ? "1.5px solid #DC2626" : inputStyle.border,
                    }}
                  />

                  <span
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      fontSize: 18,
                      opacity: 0.75,
                    }}
                  >
                    📅
                  </span>
                </div>

                {dobError && <p style={helpTextError}>{dobError}</p>}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Relationship Status</label>
                <select
                  value={applicants[activeApplicant].relationship}
                  onChange={(e) => handleChange(activeApplicant, "relationship", e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Select</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="DeFacto">DeFacto</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Separated">Separated</option>
                </select>
              </div>

              {activeApplicant === 0 && (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <label style={labelStyle}>Number of Dependants</label>
                    <select
                      value={String(applicants[0].dependants ?? "")}
                      onChange={(e) => handleChange(0, "dependants", e.target.value)}
                      style={selectStyle}
                    >
                      <option value="">Select</option>
                      <option value="0">0</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5+">5+</option>
                    </select>
                  </div>

                  {depCountA1 > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <label style={labelStyle}>Dependants Ages (years)</label>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        {depAgesA1.map((age, i) => (
                          <div key={i}>
                            <input
                              inputMode="numeric"
                              placeholder={`Dependant ${i + 1} age`}
                              value={age}
                              onChange={(e) => {
                                markTouched(0, "dependantAges")
                                const raw = e.target.value
                                const digitsOnly = String(raw || "").replace(/[^\d]/g, "")
                                const nextAges = [...depAgesA1]
                                nextAges[i] = digitsOnly === "" ? "" : clampInt(digitsOnly, 0, 30)
                                handleChange(0, "dependantAges", nextAges)
                              }}
                              style={inputStyle}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Residency Status</label>
                <select
                  value={applicants[activeApplicant].residency}
                  onChange={(e) => handleChange(activeApplicant, "residency", e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Select</option>
                  <option value="Citizen">Australian Citizen</option>
                  <option value="PR">Permanent Resident</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Credit History</label>
                <select
                  value={applicants[activeApplicant].creditHistory}
                  onChange={(e) => handleChange(activeApplicant, "creditHistory", e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Select</option>
                  <option value="Good">Good</option>
                  <option value="Average">Average</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 18, width: "100%" }}>
            <CreditGuidePanel applicantIndex={activeApplicant + 1} />
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={2500} hideProgressBar />
    </div>
  )
}

export default ApplicantStep